// Écran de connexion : e-mail, puis code à 6 chiffres reçu par e-mail. Aucun mot de passe.
import { versionLabel } from '../core/version';
import type { Store } from '../data/store';
import { el, esc } from './dom';
import { brandMark } from './icons';

const LAST_EMAIL = 'a5w:last-email';

export function renderLogin(root: HTMLElement, store: Store): void {
  let email = '';
  try { email = localStorage.getItem(LAST_EMAIL) ?? ''; } catch { /* stockage indisponible */ }
  let step: 'email' | 'code' = 'email';
  let busy = false;
  let error = '';

  const draw = (): void => {
    root.innerHTML = `<div class="login"><form class="login-card" novalidate>
      <div class="brand-lockup">${brandMark()}<span class="brand-name">ACTE·5<small>WORLD</small></span></div>
      ${step === 'email' ? `
        <div><h1>Connexion</h1><p>Recevez un lien de connexion par e-mail. Aucun mot de passe à retenir.</p></div>
        <div class="field"><label for="email">Adresse e-mail</label>
          <input id="email" type="email" autocomplete="email" inputmode="email" required value="${esc(email)}" placeholder="vous@exemple.com" ${busy ? 'disabled' : ''}></div>
        <p class="error" data-error>${esc(error)}</p>
        <button class="btn primary wide" type="submit" ${busy ? 'disabled' : ''}>${busy ? 'Envoi…' : 'Recevoir le lien de connexion'}</button>`
      : `
        <div><h1>Regardez vos e-mails</h1><p>Un lien de connexion vient d’être envoyé à <b>${esc(email)}</b>. Ouvrez-le <b>sur cet appareil</b> : vous serez connecté aussitôt. Il est valable une heure.</p></div>
        <p class="hint">Pensez au dossier « spam » si rien n’arrive dans la minute. L’expéditeur est Supabase, le service qui héberge vos données.</p>
        <details class="field"><summary class="hint" style="cursor:pointer">L’e-mail contient un code ? Saisissez-le ici.</summary>
          <label for="code" class="sr">Code reçu</label>
          <input id="code" class="code num" inputmode="numeric" autocomplete="one-time-code" maxlength="8" placeholder="······" ${busy ? 'disabled' : ''}>
          <button class="btn wide" type="submit" ${busy ? 'disabled' : ''}>${busy ? 'Vérification…' : 'Se connecter avec le code'}</button></details>
        <p class="error" data-error>${esc(error)}</p>
        <div class="card-actions" style="justify-content:space-between">
          <button class="btn ghost small" type="button" data-back>Changer d’adresse</button>
          <button class="btn ghost small" type="button" data-resend ${busy ? 'disabled' : ''}>Renvoyer le lien</button></div>`}
      <p class="hint version">${esc(versionLabel())}</p>
    </form></div>`;
    const form = el<HTMLFormElement>(root, 'form');
    form.addEventListener('submit', (e) => { e.preventDefault(); void (step === 'email' ? request() : verify()); });
    root.querySelector('[data-back]')?.addEventListener('click', () => { step = 'email'; error = ''; draw(); });
    root.querySelector('[data-resend]')?.addEventListener('click', () => void request(true));
    (root.querySelector<HTMLInputElement>(step === 'email' ? '#email' : '#code'))?.focus();
  };

  const request = async (resend = false): Promise<void> => {
    const input = root.querySelector<HTMLInputElement>('#email');
    if (input) email = input.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { error = 'Indiquez une adresse e-mail valide.'; draw(); return; }
    busy = true; error = ''; draw();
    try {
      await store.requestCode(email);
      try { localStorage.setItem(LAST_EMAIL, email); } catch { /* rien */ }
      step = 'code';
      if (resend) error = 'Un nouveau lien vient d’être envoyé.';
    } catch (e) {
      error = e instanceof Error ? e.message : 'Envoi impossible.';
    } finally { busy = false; draw(); }
  };

  const verify = async (): Promise<void> => {
    const code = root.querySelector<HTMLInputElement>('#code')?.value.trim() ?? '';
    if (code.length < 6) { error = 'Ouvrez le lien reçu par e-mail, ou saisissez le code s’il y en a un.'; draw(); return; }
    busy = true; error = ''; draw();
    try { await store.verifyCode(email, code); }
    catch (e) { error = e instanceof Error ? e.message : 'Connexion impossible.'; busy = false; draw(); }
  };

  draw();
}
